<script setup lang="ts">
import { DASHBOARD_LABELS } from '@/constants/dashboard'
import { LANDING_METRIC_RULE } from '@/content/landing'

interface MetricItem {
  label: string
  value: number | string
  color?: string
}

const props = withDefaults(defineProps<{
  dashboardData?: MetricItem[] | null
  blockNumber?: string | null
}>(), {
  dashboardData: () => [],
  blockNumber: null,
})

const ORDERED_LABELS = [
  DASHBOARD_LABELS.VAULT_APY,
  DASHBOARD_LABELS.TOTAL_DEPOSITS,
  DASHBOARD_LABELS.SPONSOR_DEPOSITS,
  DASHBOARD_LABELS.ACCRUED_REWARDS,
] as const

/** Each tile's colour and symbol: rate in blue, members' money in green, sponsor's in steel, yield in gold. */
const TILE_LOOK: Record<typeof ORDERED_LABELS[number], { hue: string, icon: string }> = {
  [DASHBOARD_LABELS.VAULT_APY]: { hue: 'var(--machine-accent-2)', icon: 'icon-[carbon--chart-line-smooth]' },
  [DASHBOARD_LABELS.TOTAL_DEPOSITS]: { hue: 'var(--machine-accent)', icon: 'icon-[carbon--wallet]' },
  [DASHBOARD_LABELS.SPONSOR_DEPOSITS]: { hue: 'var(--tier-silver)', icon: 'icon-[carbon--partnership]' },
  [DASHBOARD_LABELS.ACCRUED_REWARDS]: { hue: 'var(--tier-gold)', icon: 'icon-[carbon--growth]' },
}

/** One tile per label, always in this order. A metric that has not loaded yet keeps its tile. */
const tiles = computed(() => {
  const data = Array.isArray(props.dashboardData) ? props.dashboardData : []

  return ORDERED_LABELS.map((label, index) => ({
    label,
    value: data.find(item => item.label === label)?.value ?? null,
    note: label === DASHBOARD_LABELS.SPONSOR_DEPOSITS ? LANDING_METRIC_RULE.sponsorNote : null,
    icon: TILE_LOOK[label].icon,
    style: { '--hue': TILE_LOOK[label].hue, '--i': index },
  }))
})
</script>

<template>
  <UICard variant="band" band="metrics" class="metric-panel" aria-label="Live protocol metrics">
    <!-- Keyed on the block, so a fresh read sweeps a scan line across the panel. -->
    <span v-if="blockNumber" :key="blockNumber" class="metric-panel__scan" aria-hidden="true" />

    <div class="flex items-center justify-between gap-3">
      <p class="flex items-center gap-2">
        <span class="metric-rule__pulse" aria-hidden="true" />
        <UIText variant="kicker" as="span">
          {{ LANDING_METRIC_RULE.label }}
        </UIText>
      </p>
      <!-- The number holds a nine-digit block's width, so the label doesn't move when it lands. -->
      <UIText variant="label">
        {{ LANDING_METRIC_RULE.blockLabel }}
        <span class="ml-1 inline-block min-w-[calc(9*(1ch+0.16em))] text-right font-mono text-ink-soft tabular-nums">
          <span :key="blockNumber ?? 'none'" class="metric-panel__block">{{ blockNumber ?? '—' }}</span>
        </span>
      </UIText>
    </div>

    <dl class="metric-panel__grid mt-4">
      <div
        v-for="tile in tiles"
        :key="tile.label"
        class="metric-tile"
        :class="{ 'is-loaded': tile.value != null }"
        :style="tile.style"
      >
        <!-- The icon sits inside the dt: a dl's group may hold only dt and dd. -->
        <UIText variant="label" as="dt" class="flex items-start justify-between gap-2">
          {{ tile.label }}
          <span class="metric-tile__icon" aria-hidden="true">
            <span :class="tile.icon" class="size-4" />
          </span>
        </UIText>
        <dd class="metric-tile__value font-mono tabular-nums">
          <!-- Keyed on the figure, so it ticks in whenever a new read changes it. -->
          <span v-if="tile.value != null" :key="String(tile.value)" class="metric-tile__figure">
            {{ tile.value }}
          </span>
          <span v-else class="metric-tile__placeholder" aria-label="Loading" />
        </dd>
        <dd v-if="tile.note" class="metric-tile__note">
          {{ tile.note }}
        </dd>
      </div>
    </dl>
  </UICard>
</template>
